/* Tour "taux-de-change" — explique la consolidation multidevise : la ligne
   « FX impact » du tableau de trésorerie et la récupération des taux appliqués.

   ISOLATION : rien n'est modifié. La ligne « FX impact » n'apparaît normalement
   que pour une entreprise ayant des comptes en plusieurs devises
   (`dashboard_fx_impact_enabled`). Pour que le tuto soit montrable par tous, on
   la révèle le temps de la démo via `tour:fx-reveal` (CashTable → tourFXReveal).
   Le lien « Obtenir les taux de change » est neutralisé pendant le tuto
   (CashTable.downloadFx court-circuite l'appel back si un tour est actif) : aucun
   fichier n'est téléchargé, aucune donnée réelle touchée. */

import AppBus from '@/bus/app-bus';
import store from '@/store';
import './types';

const FAKE_BANK_KEY = 'fygr-tour-fx-bank';

/** Ajoute un faux compte en USD 100 % CLIENT-ONLY (aucun appel serveur) pour
 *  illustrer le multidevise. Le compte est `pinned: false` → il apparaît dans le
 *  dropdown « Comptes » mais N'entre PAS dans `selectedAccounts` (dérivé des
 *  comptes épinglés) → aucun fetch dashboard sur son id fictif. Retiré à la
 *  sortie du tuto. */
function seedFakeUsdBank() {
  let id = '';
  try {
    id = (window as any).utils.genId();
  } catch {
    id = `tour-fx-bank-${store.state.user?.company || 'x'}`;
  }
  try {
    localStorage.setItem(FAKE_BANK_KEY, id);
  } catch {
    /* ignore */
  }
  const banks = (store as any).state.bank?.banks;
  if (!Array.isArray(banks)) return;
  const fakeBank = {
    id,
    name: 'Compte de démonstration',
    custom_name: 'Compte de démonstration',
    aggregator: 'demo',
    type: 'demo',
    accounts: [
      {
        id: `${id}-acc`,
        name: 'Compte courant USD (démo)',
        currency: 'USD',
        balance: 24800,
        // bankBalance() divise par exchange_rate → sans ça le total banque = NaN.
        exchange_rate: 1,
        iban: null,
        pinned: false,
      },
    ],
  };
  store.commit('bank/setBanks', [...banks, fakeBank]);
}

function cleanupFakeUsdBank() {
  let id: string | null = null;
  try {
    id = localStorage.getItem(FAKE_BANK_KEY);
  } catch {
    id = null;
  }
  const banks = (store as any).state.bank?.banks;
  if (id && Array.isArray(banks)) {
    store.commit(
      'bank/setBanks',
      banks.filter((b: {id: string}) => b.id !== id),
    );
  }
  try {
    localStorage.removeItem(FAKE_BANK_KEY);
  } catch {
    /* ignore */
  }
}

/** Force la vue tableau et révèle la ligne FX impact (en rafale : la bascule de
 *  vue peut remonter CashTable après le 1er emit). */
function goToTableAndReveal() {
  AppBus.$emit('tour:dashboard-table');
  AppBus.$emit('tour:fx-reveal', true);
  let tries = 0;
  const tick = () => {
    AppBus.$emit('tour:fx-reveal', true);
    if (++tries < 6) setTimeout(tick, 400);
  };
  tick();
}

window.FygrTour.register('taux-de-change', {
  title: 'Gérer les taux de change',
  page: '/dashboard',
  intro: {
    title: 'Gérer les taux de change',
    body: 'Dès que vous détenez des comptes dans plusieurs devises, Okimia les convertit dans une même devise pour vous donner une trésorerie consolidée.',
    note: 'C’est une démonstration : rien n’est enregistré et tout est retiré à la fin du tutoriel.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote: 'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Tutoriels ».',
  },
  onStart: () => {
    // Flag persistant AVANT le seed : l'ajout du faux compte remonte la CashTable
    // (son :key dépend du nombre de banques) et réinitialiserait tourFXReveal.
    // CashTable relit ce flag à son mount pour ré-afficher la ligne FX.
    try {
      localStorage.setItem('fygr-tour-fx-reveal', '1');
    } catch {
      /* ignore */
    }
    seedFakeUsdBank();
    // Force la vue tableau + révèle la ligne FX dès le départ (la ligne ne se lit
    // qu'en vue tableau) ; la boucle de goToTableAndReveal gère aussi le
    // remontage de la CashTable provoqué par l'ajout du faux compte.
    goToTableAndReveal();
  },
  onExit: () => {
    try {
      localStorage.removeItem('fygr-tour-fx-reveal');
    } catch {
      /* ignore */
    }
    AppBus.$emit('tour:fx-reveal', false);
    cleanupFakeUsdBank();
  },
  steps: [
    {
      target: '[data-tour-step="comptes-pill"]',
      title: 'Ouvrez le menu Comptes',
      body: 'Tout commence ici : depuis « Comptes », vous ajoutez une banque ou un compte libellé dans une autre devise. Ouvrez la liste pour continuer.',
      placement: 'bottom',
      clickToAdvance: true,
      waitFor: '[data-tour-step="fx-demo-account"]',
      waitTimeout: 5000,
      // « Suivant » comme le clic sur « Comptes » doivent ouvrir le menu : si le
      // dropdown n'est pas déjà ouvert (compte démo absent du DOM), on l'ouvre
      // en cliquant la zone cliquable interne (.c-dropdown).
      onNext: () => {
        if (!document.querySelector('[data-tour-step="fx-demo-account"]')) {
          (
            document.querySelector(
              '[data-tour-step="comptes-pill"] .c-dropdown',
            ) as HTMLElement | null
          )?.click();
        }
      },
    },
    {
      target: '[data-tour-step="fx-demo-account"]',
      title: 'Votre compte de démonstration en USD',
      body: 'Pour la démonstration, nous avons ajouté ce compte d’exemple en USD.',
      placement: 'right',
      lockTarget: true,
      strictTarget: true,
      waitTimeout: 5000,
      // Laisse le dropdown Comptes se fermer (animation + overlay) avant que
      // l'étape « devise d'affichage » s'ancre, sinon l'overlay masque la pill.
      nextDelay: 400,
      onNext: () => {
        // Referme le menu Comptes via le state (un event DOM serait intercepté
        // par le moteur du tuto, cf. listener côté Dashboard).
        AppBus.$emit('tour:close-accounts-dropdown');
      },
    },
    {
      target: '[data-tour-step="dash-currency"]',
      title: 'Choisissez la devise d’affichage',
      body: 'Visualisez votre tableau de bord dans + de 150 devises. Nos taux proviennent de la Banque de France et sont historisés chaque jour.',
      placement: 'bottom',
      lockTarget: true,
      // Étape explicative : « Suivant » doit AVANCER, pas simuler un clic sur la
      // pill (verrouillée) — sinon le tuto se fige (tour interactif par défaut).
      clickToAdvance: false,
    },
    {
      // 4a — on lit l'explication (libellé surligné, non cliquable).
      target: '[data-tour-step="fx-impact-row"]',
      title: 'La ligne « FX impact »',
      body: 'La ligne « FX impact » isole l’effet du change : ce que vous gagnez ou perdez uniquement à cause des variations de taux, indépendamment de votre activité.',
      placement: 'bottom-start',
      lockTarget: true,
      clickToAdvance: false,
    },
    {
      // 4b — spotlight de TOUTE la ligne de valeurs, pour voir les variations
      // mois par mois. Même numéro d'étape que 4a (groupWithPrevious).
      target: '[data-tour-step="fx-impact-values"]',
      title: 'Les variations mois par mois',
      body: 'Sur le passé, cette ligne montre la variation de change réellement constatée, calculée au jour le jour. Sur le futur, il n’y a pas de prévision de taux : on fait l’hypothèse qu’il reste identique au taux actuel.',
      placement: 'bottom',
      lockTarget: true,
      clickToAdvance: false,
      groupWithPrevious: true,
      // Met en avant UNIQUEMENT le passé (colonnes réalisées) : flash des
      // cellules non-prévision + halo du tuto recadré sur cette zone.
      onShow: () => {
        const row = document.querySelector(
          '[data-tour-step="fx-impact-values"]',
        );
        if (!row) return;
        const pastCells = Array.from(row.querySelectorAll('td')).filter(
          (td) => !td.classList.contains('prediction'),
        ) as HTMLElement[];
        if (!pastCells.length) return;

        // Flash des cellules réalisées.
        pastCells.forEach((td) => {
          td.classList.remove('fx-tour-flash-cell');
          void td.offsetWidth;
          td.classList.add('fx-tour-flash-cell');
        });

        // Overlay transitoire couvrant la plage réalisée → on y ré-ancre le halo.
        const first = pastCells[0].getBoundingClientRect();
        const last = pastCells[pastCells.length - 1].getBoundingClientRect();
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
          position: 'fixed',
          left: `${first.left}px`,
          top: `${first.top}px`,
          width: `${last.right - first.left}px`,
          height: `${first.height}px`,
          pointerEvents: 'none',
          zIndex: '1',
        });
        document.body.appendChild(overlay);
        window.FygrTour.retarget(overlay);

        return () => {
          pastCells.forEach((td) => td.classList.remove('fx-tour-flash-cell'));
          overlay.remove();
        };
      },
    },
    {
      // 5a — l'utilisateur clique la ligne « FX impact » : ça déplie l'option
      // « Obtenir les taux de change » (toggle showFXDownload de CashTable).
      target: '[data-tour-step="fx-impact-row"]',
      title: 'Retrouvez les taux appliqués',
      body: 'Cliquez sur « FX impact » pour afficher l’option « Obtenir les taux de change ».',
      placement: 'bottom-start',
      clickToAdvance: true,
      waitFor: '[data-tour-step="fx-download"]',
      waitTimeout: 5000,
    },
    {
      // 5b — même numéro que 5a : l'export lui-même.
      target: '[data-tour-step="fx-download"]',
      title: 'Retrouvez les taux appliqués',
      body: '« Obtenir les taux de change » télécharge un fichier Excel avec l’historique des taux utilisés pour vos conversions.',
      placement: 'right',
      lockTarget: true,
      clickToAdvance: false,
      groupWithPrevious: true,
      waitTimeout: 4000,
    },
  ],
  celebration: {
    title: 'Multidevise maîtrisé',
    body: 'Ajoutez une devise depuis un compte bancaire : Okimia consolide automatiquement et fait apparaître la ligne « FX impact » sur votre tableau.',
  },
});

export {};
